import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";

export interface HttpAxiosBaseConfig {
    apiUrl: string;
    timeout?: number;
    getAccessToken: () => string | null;
    getRefreshToken: () => string | null;
    setAccessToken: (token: string) => void;
    onLogout?: () => void;
}

export interface HttpAxiosBaseInstance {
    instance: AxiosInstance;
    refreshInstance: AxiosInstance;
    updateAuthHeader: () => void;
}

export const createHttpAxiosBase = (config: HttpAxiosBaseConfig): HttpAxiosBaseInstance => {
    const {
        apiUrl,
        timeout = 30000,
        getAccessToken,
        getRefreshToken,
        setAccessToken,
        onLogout = () => {
            window.location.href = "/login";
        },
    } = config;

    const instance: AxiosInstance = axios.create({
        baseURL: apiUrl,
        timeout,
        headers: { "Content-Type": "application/json" },
    });

    const refreshInstance: AxiosInstance = axios.create({
        baseURL: apiUrl,
        timeout,
        headers: { "Content-Type": "application/json" },
    });

    // Inyecta el token en cada request sin recrear la instancia
    instance.interceptors.request.use((cfg) => {
        const access = getAccessToken();
        if (access) cfg.headers.Authorization = `Bearer ${access}`;
        return cfg;
    });

    refreshInstance.interceptors.request.use((cfg) => {
        const refresh = getRefreshToken();
        if (refresh) cfg.headers.Authorization = `Bearer ${refresh}`;
        return cfg;
    });

    const updateAuthHeader = () => {
        // No-op: el interceptor ya lee el token actualizado en cada request
    };

    const responseLogOut = () => {
        onLogout();
        return Promise.reject(new Error("Sesión expirada"));
    };

    const tryRefreshToken = async <T>(callback: () => Promise<T>): Promise<T> => {
        try {
            const response = await refreshInstance.post("auth/refresh-tokens", {});
            const newAccess = response.data?.accessToken;

            if (!newAccess) return responseLogOut();

            setAccessToken(newAccess);
            updateAuthHeader();

            return callback();
        } catch {
            return responseLogOut();
        }
    };

    const handleSuccess = <T>(resp: AxiosResponse<T>): Promise<T> => {
        console.log(`✓ ${resp.status} ${resp.config.url}`);
        return Promise.resolve(resp.data);
    };

    const handleError = async <T>(
        error: AxiosError,
        retryCallback: () => Promise<T>,
    ): Promise<T> => {
        const access = getAccessToken();

        if (error.response?.status === 401) {
            if (!access) {
                return Promise.reject(error);
            }
            return tryRefreshToken(retryCallback);
        }

        let message = "Ocurrió un error inesperado.";

        if (error.response) {
            if (typeof error.response.data === "string") {
                message = error.response.data;
            } else {
                message = `Error ${error.response.status}.`;
            }
        } else if (error.request) {
            message = "No se pudo conectar con el servidor.";
        }

        console.error(`✗ ${error.config?.url}: ${message}`);
        const appError = new Error(message) as Error & { cause?: unknown };
        appError.cause = error;
        return Promise.reject(appError);
    };

    const get = <T>(url: string, config?: object): Promise<T> =>
        instance
            .get<T>(url, config)
            .then(handleSuccess)
            .catch((err) => handleError(err, () => get<T>(url, config)));

    const post = <T>(url: string, data?: any, config?: object): Promise<T> =>
        instance
            .post<T>(url, data, config)
            .then(handleSuccess)
            .catch((err) => handleError(err, () => post<T>(url, data, config)));

    const put = <T>(url: string, data?: any, config?: object): Promise<T> =>
        instance
            .put<T>(url, data, config)
            .then(handleSuccess)
            .catch((err) => handleError(err, () => put<T>(url, data, config)));

    const patch = <T>(url: string, data?: any, config?: object): Promise<T> =>
        instance
            .patch<T>(url, data, config)
            .then(handleSuccess)
            .catch((err) => handleError(err, () => patch<T>(url, data, config)));

    const del = <T>(url: string, config?: object): Promise<T> =>
        instance
            .delete<T>(url, config)
            .then(handleSuccess)
            .catch((err) => handleError(err, () => del<T>(url, config)));

    const getWithQueryParamsSerializer = <T>(
        url: string,
        params: any,
        config?: object,
    ): Promise<T> =>
        instance
            .get<T>(url, { params, paramsSerializer: { indexes: null }, ...config })
            .then(handleSuccess)
            .catch((err) =>
                handleError(err, () => getWithQueryParamsSerializer<T>(url, params, config)),
            );

    return {
        instance,
        refreshInstance,
        updateAuthHeader,
        get,
        post,
        put,
        patch,
        delete: del,
        getWithQueryParamsSerializer,
    } as any;
};

export type HttpAxiosBase = ReturnType<typeof createHttpAxiosBase>;
